import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrder,
  devPay,
  getAlbum,
  getMe,
  getOrder,
  getPricing,
  isLocalDev,
  type Order,
  type OrderKind,
} from "../../../../lib/hostApi";
import { rupiah } from "../../../../lib/format";
import { uploadClosed } from "../../../../lib/timewindow";
import { QrImage, useQrPng } from "../../../../components/QrCard";
import { GUEST_TIERS } from "../../../../../../worker/pricing";
import { useT, type Key } from "../../../../lib/i18n";

type Search = { kind?: OrderKind };

export const Route = createFileRoute("/host/albums/$id/pay")({
  component: PayPage,
  validateSearch: (s: Record<string, unknown>): Search =>
    typeof s.kind === "string" && KINDS.some((k) => k.id === s.kind) ? { kind: s.kind as OrderKind } : {},
});

const KINDS: { id: OrderKind; labelKey: Key; topup: "photo_100" | "device_50" | "extend_30d" | null }[] = [
  { id: "package", labelKey: "host.pay.kind.package", topup: null },
  { id: "topup_photo", labelKey: "host.pay.kind.topup_photo", topup: "photo_100" },
  { id: "topup_device", labelKey: "host.pay.kind.topup_device", topup: "device_50" },
  { id: "extend_retention", labelKey: "host.pay.kind.extend_retention", topup: "extend_30d" },
];

function PayPage() {
  const { t } = useT();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const album = useQuery({ queryKey: ["host-album", id], queryFn: () => getAlbum(id) });
  const me = useQuery({ queryKey: ["me"], queryFn: getMe });
  const pricing = useQuery({ queryKey: ["pricing"], queryFn: getPricing });

  const pending = album.data?.status === "pending_payment";
  const [kind, setKind] = useState<OrderKind | null>(search.kind ?? null);
  const [method, setMethod] = useState<"qris" | "po">("qris");
  const [order, setOrder] = useState<Order | null>(null);
  const qrPng = useQrPng(order?.qr_string ?? "");

  useEffect(() => {
    if (!kind && pending) setKind("package");
  }, [kind, pending]);

  const create = useMutation({
    mutationFn: () => createOrder(id, { kind: kind ?? "package", method }),
    onSuccess: setOrder,
  });

  // Polling status; hanya webhook yang boleh mengaktifkan album.
  const status = useQuery({
    queryKey: ["order", order?.id],
    queryFn: () => getOrder(order!.id),
    enabled: !!order && order.status !== "paid",
    refetchInterval: (q) => (q.state.data?.status === "paid" ? false : 5000),
  });

  const paid = status.data?.status === "paid";
  useEffect(() => {
    if (!paid) return;
    qc.invalidateQueries({ queryKey: ["host-album", id] });
    qc.invalidateQueries({ queryKey: ["albums"] });
    const timer = setTimeout(() => navigate({ to: "/host/albums/$id", params: { id } }), 1500);
    return () => clearTimeout(timer);
  }, [paid, id, navigate, qc]);

  if (album.isPending) return <p>{t("common.loading")}</p>;
  if (album.error || !album.data) return <p>{t("host.album.not_found")}</p>;

  const canPo = me.data ? me.data.role !== "host" : false;
  const price = (k: OrderKind) => {
    const row = KINDS.find((x) => x.id === k)!;
    if (!pricing.data) return null;
    return row.topup ? pricing.data.topup[row.topup] : null;
  };

  if (paid) {
    return (
      <div className="rounded-lg border border-green-300 bg-green-50 p-4">
        <p className="font-medium">{t("host.pay.paid_title")}</p>
        <p className="text-sm text-neutral-700">{t("host.pay.paid_desc")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <a href={`/host/albums/${id}`} className="text-sm text-neutral-500 underline">
          {t("host.pay.back")}
        </a>
        <h1 className="font-serif text-2xl text-ink">{t("host.pay.title")}</h1>
        <p className="text-sm text-neutral-500">{album.data.title ?? t("host.untitled")}</p>
      </div>

      {!order ? (
        <>
          {pending && (
            <div className="card">
              <p className="font-medium">
                {t("host.pay.package_summary", { guests: album.data.device_quota, photos: album.data.device_photo_quota })}
              </p>
              {album.data.device_quota in GUEST_TIERS && (
                <p className="text-sm text-neutral-600">
                  {t("host.pay.total", { amount: rupiah(GUEST_TIERS[album.data.device_quota as keyof typeof GUEST_TIERS]) })}
                </p>
              )}
            </div>
          )}
          {!pending && (
            <fieldset className="space-y-2">
              <legend className="font-medium">{t("host.pay.choose_purchase")}</legend>
              {KINDS.filter((k) => k.id !== "package" && !(k.topup !== "extend_30d" && uploadClosed(album.data, Date.now() / 1000))).map((k) => (
                <label key={k.id} className="flex items-center gap-2 rounded-2xl border border-sand px-3 py-2">
                  <input type="radio" name="kind" checked={kind === k.id} onChange={() => setKind(k.id)} />
                  <span className="flex-1">{t(k.labelKey)}</span>
                  <span className="text-sm text-neutral-500">{price(k.id) !== null ? rupiah(price(k.id)!) : ""}</span>
                </label>
              ))}
            </fieldset>
          )}

          <fieldset className="space-y-2">
            <legend className="font-medium">{t("host.pay.method")}</legend>
            <label className="flex items-center gap-2">
              <input type="radio" name="method" checked={method === "qris"} onChange={() => setMethod("qris")} />
              QRIS
            </label>
            {canPo && pending && (
              <label className="flex items-center gap-2">
                <input type="radio" name="method" checked={method === "po"} onChange={() => setMethod("po")} />
                {t("host.pay.method_po")}
              </label>
            )}
          </fieldset>

          <button type="button" className="btn-primary w-full" disabled={!kind || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? t("host.pay.create_pending") : t("host.pay.create")}
          </button>
          {create.error && <p className="text-sm text-red-600">{create.error.message}</p>}
        </>
      ) : order.method === "po" ? (
        <div className="card">
          <p className="font-medium">{t("host.pay.po_pending_title")}</p>
          <p className="text-sm text-neutral-600">{t("host.pay.po_pending_desc", { amount: rupiah(order.amount) })}</p>
        </div>
      ) : (
        <div className="card space-y-3 text-center">
          <p className="text-lg font-semibold">{rupiah(order.amount)}</p>
          <QrImage png={qrPng} alt="QRIS" className="mx-auto w-56" />
          <p className="text-sm text-neutral-600">{t("host.pay.qris_hint")}</p>
          {isLocalDev() && (
            <button
              type="button"
              className="btn-secondary w-full border-dashed px-3 py-2 text-sm"
              onClick={() => devPay(order.id)}
            >
              {t("host.pay.dev_simulate")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
